import React, { useState, useEffect, useRef, useCallback } from 'react';
import style from './InspectionMore.module.css';
import HeadSub from '../../../../../components/comm/HeadSub';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosApi from '../../../../../api/daonAxios';
import CODE from '../../../../../constants/code';
import { BottomSheet } from 'react-spring-bottom-sheet';
import InspectionGraphBox from '../../../../../components/box/InspectionGraphBox';
import ToastPopup from '../../../../../components/comm/ToastPopup';

const PAGE_SIZE = 30;

const InspectionMore = () => {

  const [toast, setToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const location = useLocation();
  const navigate = useNavigate();

  const [examRsltList, setExamRsltList] = useState([]);
  const [openGraph, setOpenGraph] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [hasNext, setHasNext] = useState(true);

  const observerRef = useRef(null);
  const scrollObserver = useRef(null);
  const isFirstApiCalled = useRef(false);

  const [targetExamRsltList, setTargetExamRsltList] = useState({
    title: "",
    data: [],
    range: []
  });

  const onGraphDismiss = () => setOpenGraph(false);
  const handleClickOpenGraph = () => setOpenGraph(true);

  // ✅ 검사결과 호출
  const retrieveUsrExamRsltList = useCallback(async (pageNo) => {
    const url = "/mypage/retrieveUsrExamRsltList";
    const jToken = localStorage.getItem('jToken');
    const reqOptions = { headers: { Authorization: jToken } };

    await axiosApi.post(url, {pageNo: pageNo, pageSize: PAGE_SIZE }, reqOptions)
    .then((res) => {
        if (res.data.resultCode === CODE.RCV_SUCCESS) {
            setExamRsltList(prev => [...prev, ...res.data.result.examRsltList]);
            if (!res.data.result.hasNext) setHasNext(false);
        } else {
            setHasNext(false);
            setToastMessage(res.data.resultMessage);
            setToast(true);
        }
    })
    .catch(err => {
        setHasNext(false);
        console.error("주요검사 결과 호출 중 오류  : ", err);
    })
  }, []);

  // ✅ 최초 한 번만 호출
  useEffect(() => {
    if (!isFirstApiCalled.current) {
      isFirstApiCalled.current = true;
      setTimeout(() => {
        retrieveUsrExamRsltList(1);
      }, 50);
    }
  }, []);

  // ✅ pageNo 변경 시 (2 이상) 호출
  useEffect(() => {
    if (pageNo > 1 && hasNext) {
      retrieveUsrExamRsltList(pageNo);
    }
  }, [pageNo]);

  // ✅ observer는 데이터가 렌더링된 이후에만 등록
  useEffect(() => {
    if (!hasNext || examRsltList.length === 0) return;

    // 기존 observer 제거
    if (scrollObserver.current && observerRef.current) {
      scrollObserver.current.unobserve(observerRef.current);
    }

    scrollObserver.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        setPageNo(prev => prev + 1);
      }
    }, {
      root: null,
      rootMargin: '0px',
      threshold: 1.0
    });

    if (observerRef.current) {
      scrollObserver.current.observe(observerRef.current);
    }

    return () => {
      if (scrollObserver.current && observerRef.current) {
        scrollObserver.current.unobserve(observerRef.current);
      }
    };
  }, [examRsltList.length, hasNext]);

  const retrieveTargetExamRsltList = useCallback(async (title, range, hsptlCd, prscrptnTestCd) => {
    const url = "/mypage/retrieveTargetExamRsltList";
    const jToken = localStorage.getItem('jToken');
    const reqOptions = { headers: { Authorization: jToken } };

    await axiosApi.post(url, { hsptlCd, prscrptnTestCd }, reqOptions)
    .then((res) => {
        if (res.data.resultCode === CODE.RCV_SUCCESS) {
            const data = res.data.result.targetExamRsltList ?? [];

            setTargetExamRsltList({title, range, data: data.reverse()});
            handleClickOpenGraph();
        } else {
            setToastMessage(res.data.resultMessage);
            setToast(true);
        }
    })
    .catch(err => {
        console.error("주요검사 결과 호출 중 오류  : ", err);
    })
  }, []);

  if (!examRsltList) return null;

  return (
    <>
      <HeadSub title="주요 검사 결과" />

      <div id="sub-content">
        <div className='inner-wrap' data-pad="normal">
          <div className={style["inspection-list-box"]}>
            <ul>
              {examRsltList.length === 0 &&
                <li className={style['no-data-box']}>
                  <span>주요 검사 결과가 없습니다.</span>
                </li>
              }

              {examRsltList.length > 0 &&
                examRsltList.map((item, _idx) => (
                  <li key={_idx}>
                    <div className={style["list-box"]}>
                      <div className={style["top-head"]}>
                        {item.isInRef === '-99' ? <span data-status=''></span>
                          : item.isInRef === '0' ? <span data-status='strange'>이상</span>
                            : <span data-status="normal">정상</span>}
                        <div className={style['btns-box']}>
                          <button
                            className={style['graph-more-btn']}
                            onClick={() => retrieveTargetExamRsltList(
                              item.testKorNm,
                              [item.refMin, item.refMax],
                              item.hsptlCd,
                              item.prscrptnTestCd
                            )}
                          />
                        </div>
                      </div>

                      <div className={style["head"]}>
                        <p>{item.testKorNm} <span>{item.testEngNm}</span></p>
                      </div>

                      <div className={style["value-box"]}>
                        <strong>{item.prscrptnTestRsltVal} {item.unit}</strong>
                        <p className={style['compar-value']} data-status={
                          item.diffVal === 0 || !item.diffVal ? 'same'
                            : item.diffVal > 0 ? 'up' : 'down'
                        }>
                          (<span>{item.diffVal !== 0 ? item.diffVal : ""}</span>)
                        </p>
                      </div>

                      <p className={style["details-txt"]}>
                        <span>{item.testKorDesc}</span>
                      </p>

                      <div className={style['span-box']}>
                        <span>{item.prscrptnTestRsltDate}</span>
                        <span>{item.hsptlNm}</span>
                      </div>
                    </div>
                  </li>
                ))
              }
            </ul>

            {/* 👇 관찰 대상 */}
            <div ref={observerRef} style={{ height: '1px' }} />
          </div>

          <BottomSheet

            scrollLocking={false} // 내부 스크롤을 위해 BottomSheet 스크롤 잠금 해제
            expandOnContentDrag={false} // 내용 드래그로 확장 방지

            open={openGraph}
            onDismiss={onGraphDismiss}
            snapPoints={({ minHeight }) => minHeight}
            blocking={true}
          >
            <InspectionGraphBox
              onClose={onGraphDismiss}
              title={targetExamRsltList.title}
              data={targetExamRsltList.data}
              range={targetExamRsltList.range}
            />
          </BottomSheet>
        </div>
      </div>
      {toast && (
          <ToastPopup
              setToast={setToast}
              message={toastMessage}
              position="center"
              opacity="90"
          />
      )}
    </>
  );
};

export default InspectionMore;
